// Linked-video playback for the main gallery area and the lightbox.

const videoMethods = {
    /**
     * Play the video linked to the current image item.
     * Uses the existing mainIframe / mainVideo infrastructure so all the
     * existing options (videoEmbedFitContainer, videoHostedAutoplay, etc.)
     * apply automatically.
     */
    _playLinkedVideo() {
        const item = this.items[this.index];
        if (!item || !item.linkedVideoType) return;

        // Snapshot dimensions BEFORE hiding the image — once display:none is
        // applied the container collapses and offsetHeight returns 0, which
        // would lock videoHostedFitContainer to a zero-height box.
        const snapW = this.mainDisplay.offsetWidth;
        const snapH = this.mainDisplay.offsetHeight;

        // Hide image + play button; stop any active zoom session and disable zoom.
        this.mainImg.style.display = "none";
        if (this._linkedVideoBtn)
            this._linkedVideoBtn.style.display = "none";
        this._zoomLeave();
        this.mainWrap.classList.add("wcpg-no-zoom");
        this.mainWrap.classList.add("wcpg-linked-video-active");
        this.lens.style.visibility = "hidden";

        if (
            item.linkedVideoType === "youtube" ||
            item.linkedVideoType === "vimeo"
        ) {
            if (this.mainIframe) {
                if (this.opts.videoEmbedFitContainer) {
                    const cH = snapH || Math.round((snapW * 9) / 16);
                    if (!this.mainDisplay.style.height) {
                        this.mainDisplay.style.height = cH + "px";
                    }
                    const videoRatio = 16 / 9;
                    const containerRatio = snapW / cH;
                    let iW, iH;
                    if (containerRatio >= videoRatio) {
                        iW = snapW;
                        iH = Math.round(snapW / videoRatio);
                    } else {
                        iH = cH;
                        iW = Math.round(cH * videoRatio);
                    }
                    Object.assign(this.mainIframe.style, {
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        width: iW + "px",
                        height: iH + "px",
                        transform: "translate(-50%,-50%)",
                        aspectRatio: "unset",
                    });
                } else {
                    this._resetIframeCoverStyles();
                }
                // Append autoplay param so the video starts immediately.
                let embedSrc = item.linkedVideoEmbed || "";
                if (embedSrc && embedSrc.indexOf("autoplay=") === -1) {
                    embedSrc +=
                        (embedSrc.indexOf("?") === -1 ? "?" : "&") +
                        "autoplay=1";
                    if (
                        embedSrc.indexOf("mute=") === -1 &&
                        embedSrc.indexOf("muted=") === -1
                    ) {
                        embedSrc += "&mute=1"; // required by most browsers
                    }
                }
                this.mainIframe.src = embedSrc;
                this.mainIframe.style.display = "block";
                this.mainVideo.style.display = "none";
            }
        } else {
            // Hosted / direct URL
            this._resetIframeCoverStyles();
            if (this.mainIframe) {
                this.mainIframe.style.display = "none";
                this.mainIframe.src = "";
            }
            if (this.opts.videoHostedFitContainer) {
                if (!this.mainDisplay.style.height) {
                    this.mainDisplay.style.height = snapH + "px";
                }
                this.mainVideo.classList.add("wcpg-fit");
            }
            this.mainVideo.src = item.linkedVideo;
            this.mainVideo.muted = this.opts.videoHostedMuted;
            this.mainVideo.style.display = "block";
            this.mainVideo.load();
            this.mainVideo.play().catch(() => {});
        }
    },

    /**
     * Stop an active linked-video session and restore the main image.
     * Passing silent=true skips the DOM restore (used when _renderItem
     * is about to overwrite everything anyway).
     */
    _stopLinkedVideo(silent = false) {
        if (!this.mainWrap.classList.contains("wcpg-linked-video-active"))
            return;
        this.mainWrap.classList.remove("wcpg-linked-video-active");
        if (!silent) {
            this.mainVideo.pause();
            this.mainVideo.src = "";
            this.mainVideo.style.display = "none";
            if (this.mainIframe) {
                this.mainIframe.src = "";
                this.mainIframe.style.display = "none";
            }
            this.mainImg.style.display = "";
            const item = this.items[this.index];
            if (this._linkedVideoBtn && item && item.linkedVideoType) {
                this._linkedVideoBtn.style.display = "";
            }
        }
    },

    // ── Lightbox: play linked video attached to the current image ────────────────
    _lbPlayLinkedVideo() {
        const lb = this.constructor._lbEl;
        if (!lb) return;
        const item = this.items[this._lbIdx];
        if (!item || !item.linkedVideoType) return;

        const lbImg = lb.querySelector(".wcpg-lb-img");
        const lbVid = lb.querySelector(".wcpg-lb-video");
        const lbIframe = lb.querySelector(".wcpg-lb-iframe");
        const lbLinkedVideoBtn = lb.querySelector(".wcpg-lb-linked-video-btn");

        lbImg.style.display = "none";
        if (lbLinkedVideoBtn) lbLinkedVideoBtn.style.display = "none";

        this._lbStopAutoplay();

        if (item.linkedVideoType === "youtube" || item.linkedVideoType === "vimeo") {
            if (lbVid) lbVid.style.display = "none";
            if (lbIframe) {
                let embedSrc = item.linkedVideoEmbed || "";
                if (embedSrc && embedSrc.indexOf("autoplay=") === -1) {
                    embedSrc +=
                        (embedSrc.indexOf("?") === -1 ? "?" : "&") + "autoplay=1";
                    if (
                        embedSrc.indexOf("mute=") === -1 &&
                        embedSrc.indexOf("muted=") === -1
                    ) {
                        embedSrc += "&mute=1";
                    }
                }
                lbIframe.src = embedSrc;
                lbIframe.style.display = "block";
            }
        } else {
            if (lbIframe) {
                lbIframe.src = "";
                lbIframe.style.display = "none";
            }
            if (lbVid) {
                lbVid.loop = this.opts.videoHostedLoop;
                lbVid.muted = this.opts.videoHostedMuted;
                lbVid.src = item.linkedVideo;
                lbVid.load();
                lbVid.style.display = "block";
                lbVid.play().catch(() => {});
            }
        }

        this._lbSyncToolbar();
    },
};

export default videoMethods;
